import moment from 'moment-timezone';
import colorsVars from 'styles/colors.module.scss';
import { addTestIdsToElements, formatValueForGraph } from 'utilitys/helpers/general';

import constants from 'components/common/Constants/constants';

const { runratePastPlannedLight, runratePastActualLight, runratePastPlannedDark, runratePastActualDark } = colorsVars;

export const categoriesKeys = [
    {
        key: 'frequencyCount',
        label: 'Frequency Count',
        color: {
            light: runratePastActualLight,
            dark: runratePastActualDark,
        },
    },
    {
        key: 'percentOfProbability',
        label: 'Probability %',
        color: {
            light: runratePastPlannedLight,
            dark: runratePastPlannedDark,
        },
    },
];

export const serializeData = (data) => {
    return [
        {
            name: `${categoriesKeys.find((item) => item.key === 'frequencyCount')?.label} (per ${data.period})`,
            type: 'column',
            data: data.barSeries.data.map((item) => item.value),
        },
        {
            name: categoriesKeys.find((item) => item.key === 'percentOfProbability')?.label,
            type: 'line',
            data: data.lineSeries.data.map((item) => item.value),
        },
    ];
};

export const getFormat = (period) => {
    switch (period) {
        case 'day':
        case 'week':
            return constants.formats.date.default; // 'DD MMM YYYY'
        case 'month':
            return constants.formats.date.monthAndYear; // 'MMM yyyy'
        case 'year':
            return constants.formats.date.fullYear; // 'YYYY'
        default:
            return constants.formats.date.default; // 'DD MMM YYYY'
    }
};

export const serializeOptions = ({ data, themeStatus }): ApexCharts.ApexOptions => {
    const periodFormat = constants.formats.date.default;
    return {
        plotOptions: {
            bar: {
                columnWidth: '50%',
                borderRadius: 0,
            },
        },
        states: {
            active: {
                filter: {
                    type: 'darken',
                },
            },
            hover: {
                filter: {
                    type: 'darken',
                },
            },
        },
        stroke: {
            width: [0, 2],
            curve: 'smooth',
        },
        colors: categoriesKeys.map((item) => (themeStatus ? item.color.light : item.color.dark)),
        chart: {
            background: themeStatus ? '#000000' : '#ffffff',
            foreColor: themeStatus ? '#FEFEFE' : '#000000',
            toolbar: {
                show: true,
                offsetX: 0,
                offsetY: 0,
                tools: {
                    download: false,
                    selection: false,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: true,
                },
            },
            events: {
                beforeZoom(chart: any) {
                    if (chart.wheelDelay) {
                        return {
                            xaxis: {
                                min: chart.minX,
                                max: chart.maxX,
                            },
                        };
                    }
                    return undefined;
                },
                updated: () => {
                    addTestIdsToElements('.apexcharts-bar-series .apexcharts-series path', 'distribution-graph-chart');
                },
            },
        },
        legend: {
            itemMargin: {
                horizontal: 10,
                vertical: 25,
            },
        },
        xaxis: {
            type: 'datetime',
            categories: data.barSeries.data.map((item) => moment(item.label).format(periodFormat)),
            labels: {
                datetimeUTC: false,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM yyyy',
                    day: 'dd MMM',
                },
                hideOverlappingLabels: true,
            },
            axisBorder: {
                show: false,
            },
        },
        yaxis: [
            {
                opposite: false,
                max: undefined,
                title: {
                    text: categoriesKeys.find((item) => item.key === 'frequencyCount')?.label,
                },
                tickAmount: 5,
                decimalsInFloat: 0,
            },
            {
                opposite: true,
                min: 0,
                max: 100,
                title: {
                    text: categoriesKeys.find((item) => item.key === 'percentOfProbability')?.label,
                },
                labels: {
                    style: {
                        fontSize: '1.2rem',
                    },
                    formatter: (value) => {
                        return value === undefined ? 'N/A' : `${formatValueForGraph(value)}%`;
                    },
                },
            },
        ],
        tooltip: {
            theme: themeStatus ? 'dark' : 'light',
        },
    };
};

export const exportCsvHeaders = [
    {
        key: 'date',
        name: 'Date',
    },
    {
        key: 'frequencyCount',
        name: 'Frequency Count',
    },
    {
        key: 'percentOfProbability',
        name: 'Probability %',
    },
];
